import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { Search, ArrowDown, ArrowUp } from 'lucide-react';
import SortFilterControl, { SORT_FIELDS } from '../../../components/ui/SortFilterControl';
import { loadPortfolioOptions, loadMarketValueChange } from '../../reports/lib/portfolioOptions';

const NAVY = '#0A0A0A';
const NAVY_SOFT = '#141414';
const CREAM = '#FFFFFF';
const GOLD = '#FFFFFF';
const HAIRLINE = 'rgba(255, 255, 255, 0.10)';
const HAIRLINE_STRONG = 'rgba(255, 255, 255, 0.22)';
// Day-over-day figures on the Market Value cell: green when a value rose (position
// gained, or a net client inflow), red when it fell (position lost, or an outflow).
const CHANGE_UP = '#16a34a';
const CHANGE_DOWN = '#dc2626';

// Custodian codes from the data lake -> display labels for the Broker column.
const CUSTODIAN_LABELS = { pershing: 'Pershing', apx: 'APX' };
const formatCustodian = (accounts) => {
  const names = [...new Set((accounts || []).map((a) => a.custodian).filter(Boolean))];
  if (!names.length) return '–';
  return names
    .map((c) => CUSTODIAN_LABELS[c.toLowerCase()] || c.charAt(0).toUpperCase() + c.slice(1))
    .join(', ');
};

// "Broker + BrokerID". The broker label always renders; the last 4 of the
// account number is appended ONLY when the row is backed by exactly ONE
// account. A multi-account client - and every family row - gets the label
// alone: an id drawn from one of N accounts would misidentify the row.
const formatBroker = (accounts, accountNumbers) => {
  const label = formatCustodian(accounts);
  return accountNumbers.length === 1 ? `${label} ${accountNumbers[0].slice(-4)}` : label;
};

// "YYYY-MM-DD" -> "MM/DD/YYYY". The em-dash marks an account the lake carries
// no ACCOUNT_BASE.StartDate for (84 of 687 rows as of 2026-07-30).
const formatInception = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '–';
};

// Whole-dollar market value (e.g. "$1,234,567"). The em-dash stands in for
// families with no priced holdings (market_value 0 / unmapped), matching the
// empty-cell convention used by the Broker and Inception Date columns.
const MV_FORMAT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
// Format any real total (including genuine negatives, e.g. net-liability
// accounts); only an exact 0 — an unmapped family with no priced holdings —
// degrades to the em-dash.
const formatMarketValue = (v) => (v !== 0 ? MV_FORMAT.format(v) : '–');

// Explicitly-signed whole-dollar figure (e.g. "+$123,456" / "-$98,765") for the
// day-over-day position-change and net-flow lines.
const formatSigned = (v) => `${v >= 0 ? '+' : '-'}${MV_FORMAT.format(Math.abs(v))}`;

// "YYYY-MM-DD" -> "MM/DD" for the compact hover title.
const shortDate = (iso) => {
  const m = /^\d{4}-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[1]}/${m[2]}` : '';
};

// The firm's three portfolio groups (Groups.xlsx -> Postgres client_groups).
// Master = "All Accounts", so its tab always lists every portfolio; Managed and
// Trading are membership-filtered against each group's member account numbers
// returned by /codenames. Family is a different row model entirely - one row per
// client_groups row with kind='family' (the same 46-family roster @/@@ report
// generation resolves against), not one row per client. Tab order matches the
// firm's mental model.
const TABS = [
  { key: 'REDACTED-CLIENT', label: 'Master' },
  { key: 'MANAGED', label: 'Managed' },
  { key: 'TRADING', label: 'Trading' },
  { key: 'FAMILY', label: 'Family' },
];

// Sort dimensions offered per tab. "# Accounts" is Family-only (no other tab
// renders the column) and Inception Date is never on the Family tab, so the
// menu can never offer a dimension the visible table does not show.
const FAMILY_SORT_FIELDS = [
  { key: SORT_FIELDS.MARKET_VALUE, label: 'Market value' },
  { key: SORT_FIELDS.ACCOUNT_COUNT, label: 'Number of accounts' },
  { key: SORT_FIELDS.ALPHA, label: 'Alphabetical' },
];
const CLIENT_SORT_FIELDS = [
  { key: SORT_FIELDS.MARKET_VALUE, label: 'Market value' },
  { key: SORT_FIELDS.INCEPTION, label: 'Inception date' },
  { key: SORT_FIELDS.ALPHA, label: 'Alphabetical' },
];
// Which column header carries the sort glyph for each dimension.
const SORT_COLUMN = {
  [SORT_FIELDS.MARKET_VALUE]: 'Market Value',
  [SORT_FIELDS.ACCOUNT_COUNT]: '# Accounts',
  [SORT_FIELDS.INCEPTION]: 'Inception Date',
  [SORT_FIELDS.ALPHA]: 'Name',
};

const PortfolioOverviewPage = () => {
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [portfolios, setPortfolios] = useState([]);
  // One row per client_groups row with kind='family' (Family tab only).
  const [families, setFamilies] = useState([]);
  // codename -> Set(account_number) for each group, used to bucket portfolios.
  const [groupMembers, setGroupMembers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('REDACTED-CLIENT');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: SORT_FIELDS.MARKET_VALUE, dir: 'desc' });
  // Map(account_number -> { mv, prev_mv, net_flow, position_change, as_of,
  // prev_as_of }) | null. Fetched fresh (market value is the default view) so the
  // tab shows real values + a day-over-day position change and net client flow
  // even when the login-preload market-value aggregate timed out on a cold lake.
  const [mvChange, setMvChange] = useState(null);

  // Load the firm's real portfolios (client families) and group memberships
  // through loadPortfolioOptions() — the SAME shared, login-preloaded /codenames
  // cache the Reports account picker uses. Both surfaces pull from one place and
  // the payload is fetched once per session. Each client family is one row;
  // group membership powers the Master/Managed/Trading tabs.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await loadPortfolioOptions();
        if (cancelled) return;
        // Per-account market value and inception date are carried on the flat
        // `accounts` list, not on each client. Index it so a row's total is the
        // sum across its accounts and its inception the earliest of them.
        const acctMeta = new Map((data.accounts || []).map((a) => [a.account_number, a]));
        const sumMv = (accountNumbers) =>
          accountNumbers.reduce((sum, an) => sum + (acctMeta.get(an)?.market_value || 0), 0);
        const rows = (data.clients || [])
          .map((c) => {
            const accountNumbers = (c.accounts || [])
              .map((a) => a.account_number)
              .filter(Boolean);
            return {
              id: c.client_id,
              // Short reference code (e.g. "AKER"). The "@" is a display affix the
              // report picker adds for client families; strip it for the column.
              codename: (c.codename || '').replace(/^@/, ''),
              name: c.name,
              accountCount: c.account_count,
              broker: formatBroker(c.accounts, accountNumbers),
              // Earliest account-opened date across the row's accounts.
              inceptionDate: accountNumbers.reduce((min, an) => {
                const d = acctMeta.get(an)?.inception_date;
                return d && (!min || d < min) ? d : min;
              }, null),
              accountNumbers,
              marketValue: sumMv(accountNumbers),
            };
          })
          // Rank by market value (largest first); fall back to name for ties and
          // for families with no priced holdings.
          .sort(
            (a, b) =>
              b.marketValue - a.marketValue || (a.name || '').localeCompare(b.name || '')
          );
        // Family rows come straight off families[] - the same roster @/@@ report
        // generation resolves against, so the two surfaces can never disagree.
        // `members` is the account numbers; `account_count` is the member-CLIENT
        // count, which is NOT what the "# Accounts" column means.
        const familyRows = (data.families || []).map((f) => {
          const accountNumbers = f.members || [];
          return {
            id: `fam:${f.codename}`,
            kind: 'family',
            codename: f.codename,
            name: f.name || f.codename,
            accountCount: accountNumbers.length,
            broker: formatCustodian(accountNumbers.map((an) => acctMeta.get(an) || {})),
            accountNumbers,
            marketValue: sumMv(accountNumbers),
          };
        });
        // Index each group's member account numbers for client-side bucketing.
        // Normalize codename casing so the tab keys (REDACTED-CLIENT/MANAGED/TRADING)
        // match regardless of how a group was created (seed vs Manual widget).
        const members = {};
        (data.groups || []).forEach((g) => {
          members[(g.codename || '').toUpperCase()] = new Set(g.members || []);
        });
        setPortfolios(rows);
        setFamilies(familyRows);
        setGroupMembers(members);
      } catch (e) {
        if (!cancelled) setError('Failed to load portfolios');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch fresh per-account market values + prior-snapshot values for display and
  // the day-over-day change arrows. Independent of the login preload (whose MV can
  // degrade to zeros), so the column shows real dollars even then. Fail-soft: on
  // error mvChange stays null and the tab falls back to the preload MV (no arrows).
  useEffect(() => {
    let cancelled = false;
    loadMarketValueChange()
      .then((map) => {
        if (!cancelled) setMvChange(map);
      })
      .catch(() => {
        /* keep null -> fall back to preload market values */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const textPrimary = darkMode ? CREAM : NAVY;
  const textMuted = darkMode ? 'rgba(255,255,255,0.50)' : 'rgba(0,0,0,0.50)';
  const hairline = darkMode ? HAIRLINE : 'rgba(0,0,0,0.10)';
  const hairlineStrong = darkMode ? HAIRLINE_STRONG : 'rgba(0,0,0,0.22)';
  const rowHover = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
  const tableHeaderBg = darkMode ? NAVY_SOFT : 'rgba(10,23,51,0.06)';

  // Master lists every portfolio (definition: "All Accounts"); Managed and
  // Trading include a portfolio when any of its accounts is in that group.
  const inGroup = (portfolio, tabKey) => {
    if (tabKey === 'REDACTED-CLIENT') return true;
    const set = groupMembers[tabKey];
    return !!set && portfolio.accountNumbers.some((a) => set.has(a));
  };

  const isFamilyTab = activeTab === 'FAMILY';
  // A tab can only sort on a dimension it renders, so a stale selection (e.g.
  // "# Accounts" carried off the Family tab) falls back to market value.
  const sortFields = isFamilyTab ? FAMILY_SORT_FIELDS : CLIENT_SORT_FIELDS;
  const sortField = sortFields.some((f) => f.key === sortConfig.field)
    ? sortConfig.field
    : SORT_FIELDS.MARKET_VALUE;

  const counts = useMemo(() => {
    const c = { FAMILY: families.length };
    TABS.filter((t) => t.key !== 'FAMILY').forEach((t) => {
      c[t.key] = portfolios.filter((p) => inGroup(p, t.key)).length;
    });
    return c;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolios, families, groupMembers]);

  const visible = useMemo(() => {
    const mc = mvChange;
    // Fold in the fresh per-account feed (when loaded), overriding the login-preload
    // MV: sums the family's current value, its day-over-day position change, and its
    // net client flow. null position_change / net_flow are treated as missing (not 0),
    // so a family shows a change line only when at least one account has a comparison.
    const withMv = (p) => {
      // Only override the preload market value when the fresh feed actually covers
      // this family; otherwise keep the preload total (no change line). This
      // preserves the codename MV when the feed is empty (failed / still loading)
      // or doesn't cover the account.
      const hasFresh = mc && p.accountNumbers.some((an) => mc.get(an) !== undefined);
      if (!hasFresh) {
        return { ...p, positionChange: 0, netFlow: null, hasComparison: false };
      }
      let mv = 0;
      let positionChange = 0;
      let netFlow = null;
      let hasComparison = false;
      let asOf = null;
      let prevAsOf = null;
      p.accountNumbers.forEach((an) => {
        const e = mc.get(an);
        if (!e) return;
        if (typeof e.mv === 'number') mv += e.mv;
        if (e.position_change != null) {
          positionChange += e.position_change;
          hasComparison = true;
          asOf = e.as_of || asOf;
          prevAsOf = e.prev_as_of || prevAsOf;
        }
        if (e.net_flow != null) netFlow = (netFlow || 0) + e.net_flow;
      });
      return {
        ...p,
        marketValue: mv,
        positionChange: hasComparison ? positionChange : 0,
        netFlow,
        hasComparison,
        asOf,
        prevAsOf,
      };
    };
    const rows = (isFamilyTab ? families : portfolios)
      .filter(
        (p) =>
          (isFamilyTab || inGroup(p, activeTab)) &&
          (!searchQuery || (p.name || '').toLowerCase().includes(searchQuery.toLowerCase()))
      )
      .map(withMv);
    // Re-sort here (not only at load) because the fresh MV can reorder rows
    // relative to the preload-time ordering.
    const dir = sortConfig.dir === 'asc' ? 1 : -1;
    const compare = {
      [SORT_FIELDS.MARKET_VALUE]: (a, b) => (a.marketValue - b.marketValue) * dir,
      [SORT_FIELDS.ACCOUNT_COUNT]: (a, b) => (a.accountCount - b.accountCount) * dir,
      [SORT_FIELDS.ALPHA]: (a, b) => (a.name || '').localeCompare(b.name || '') * dir,
      [SORT_FIELDS.INCEPTION]: (a, b) => {
        // "Unknown" is not a date: rows with no inception sink to the bottom in
        // BOTH directions rather than leading an ascending list.
        if (!a.inceptionDate) return b.inceptionDate ? 1 : 0;
        if (!b.inceptionDate) return -1;
        return a.inceptionDate.localeCompare(b.inceptionDate) * dir;
      },
    }[sortField];
    return rows.sort(
      (a, b) => compare(a, b) || (a.name || '').localeCompare(b.name || '')
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolios, families, activeTab, searchQuery, sortConfig, sortField, groupMembers, mvChange]);

  const RIGHT_ALIGN_COLS = new Set(['Market Value', '# Accounts']);
  const columns = isFamilyTab
    ? ['Shortname', 'Name', 'Broker + BrokerID', '# Accounts', 'Market Value']
    : ['Shortname', 'Name', 'Broker + BrokerID', 'Inception Date', 'Market Value'];
  const activeSortCol = SORT_COLUMN[sortField];

  const openPortfolio = (portfolio) => {
    navigate(`/client/${portfolio.id}`, { state: { name: portfolio.name } });
  };

  const emptyMessage = searchQuery
    ? 'No portfolios match your search'
    : isFamilyTab
      ? 'No families found'
      : activeTab === 'REDACTED-CLIENT'
        ? 'No portfolios found'
        : 'No portfolios in this group';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        padding: 24,
        boxSizing: 'border-box',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <h1
          className="font-display"
          style={{
            fontSize: 'clamp(1.5rem, 3vw, 2.25rem)',
            fontWeight: 400,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: textPrimary,
            margin: 0,
          }}
        >
          Portfolios
        </h1>
      </div>

      {/* Group tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${hairline}` }}>
        {TABS.map((tab) => {
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '8px 14px',
                border: 'none',
                background: 'transparent',
                color: active ? textPrimary : textMuted,
                fontSize: 14,
                fontWeight: active ? 600 : 400,
                fontFamily: 'Inter, sans-serif',
                cursor: 'pointer',
                borderBottom: active ? `2px solid ${GOLD}` : '2px solid transparent',
                marginBottom: -1,
                transition: 'color 150ms',
              }}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.color = textPrimary;
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.color = textMuted;
              }}
            >
              {tab.label}
              <span style={{ fontSize: 12, fontWeight: 400, color: textMuted }}>{counts[tab.key] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {/* Search and Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 380 }}>
          <Search
            size={15}
            style={{
              position: 'absolute',
              left: 12,
              top: '50%',
              transform: 'translateY(-50%)',
              color: textMuted,
              pointerEvents: 'none',
            }}
          />
          <input
            type="text"
            placeholder="Search portfolios"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              paddingLeft: 36,
              paddingRight: 14,
              paddingTop: 8,
              paddingBottom: 8,
              borderRadius: 6,
              border: `1px solid ${hairlineStrong}`,
              background: darkMode ? NAVY_SOFT : 'rgba(255,255,255,0.7)',
              color: textPrimary,
              fontSize: 14,
              fontFamily: 'Inter, sans-serif',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <SortFilterControl
          value={sortConfig}
          onChange={setSortConfig}
          fields={sortFields}
          darkMode={darkMode}
        />
      </div>

      {/* Portfolios Table (scrollable) */}
      <div
        className="custom-scrollbar"
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          borderRadius: 4,
          border: `1px solid ${hairline}`,
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {columns.map((col) => {
                const isNumeric = RIGHT_ALIGN_COLS.has(col);
                const isSortCol = col === activeSortCol;
                return (
                  <th
                    key={col}
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      background: tableHeaderBg,
                      padding: '10px 20px',
                      textAlign: isNumeric ? 'right' : 'left',
                      fontSize: 11,
                      fontWeight: 500,
                      fontFamily: 'Inter, sans-serif',
                      textTransform: 'uppercase',
                      letterSpacing: '0.18em',
                      color: textMuted,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isSortCol ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          justifyContent: isNumeric ? 'flex-end' : 'flex-start',
                        }}
                      >
                        {col}{' '}
                        {sortConfig.dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                      </span>
                    ) : col}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: '24px 20px', fontSize: 14, color: textMuted, textAlign: 'center' }}>
                  Loading portfolios…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: '24px 20px', fontSize: 14, color: textMuted, textAlign: 'center' }}>
                  {error}
                </td>
              </tr>
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: '24px 20px', fontSize: 14, color: textMuted, textAlign: 'center' }}>
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              visible.map((portfolio) => {
                // Only client rows have a detail page (/client/:id); a family is
                // not a client, so its row is inert rather than a dead link.
                const clickable = portfolio.kind !== 'family';
                return (
                <tr
                  key={portfolio.id}
                  onClick={clickable ? () => openPortfolio(portfolio) : undefined}
                  style={{
                    cursor: clickable ? 'pointer' : 'default',
                    borderTop: `1px solid ${hairline}`,
                    background: 'transparent',
                    transition: 'background 120ms',
                  }}
                  onMouseEnter={(e) => {
                    if (clickable) e.currentTarget.style.background = rowHover;
                  }}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '12px 20px', fontSize: 14, color: textMuted, whiteSpace: 'nowrap' }}>
                    {portfolio.codename || '–'}
                  </td>
                  <td
                    style={{
                      padding: '12px 20px',
                      fontSize: 14,
                      fontWeight: 500,
                      color: textPrimary,
                      maxWidth: 300,
                    }}
                    title={portfolio.name}
                  >
                    {/* Truncate long full names with an ellipsis so the table fits
                        the page cleanly; the full name shows on hover. */}
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {portfolio.name}
                    </div>
                  </td>
                  <td style={{ padding: '12px 20px', fontSize: 14, color: textMuted, whiteSpace: 'nowrap' }}>
                    {portfolio.broker}
                  </td>
                  {isFamilyTab ? (
                    <td
                      style={{
                        padding: '12px 20px',
                        fontSize: 14,
                        color: textMuted,
                        whiteSpace: 'nowrap',
                        textAlign: 'right',
                      }}
                    >
                      {portfolio.accountCount}
                    </td>
                  ) : (
                    <td style={{ padding: '12px 20px', fontSize: 14, color: textMuted, whiteSpace: 'nowrap' }}>
                      {formatInception(portfolio.inceptionDate)}
                    </td>
                  )}
                  <td
                    style={{
                      padding: '12px 20px',
                      fontSize: 14,
                      whiteSpace: 'nowrap',
                      textAlign: 'right',
                    }}
                  >
                    {(() => {
                      // Line 1: the family total, always neutral (no tint / arrow).
                      // Line 2 (only with a comparison): a signed position-change
                      // figure - green + up arrow when the family's position value
                      // rose vs the prior performance day, red + down when it fell,
                      // neutral gray with no arrow at exactly 0 - plus a signed
                      // net-flow figure (green + up = deposit, red + down = withdrawal)
                      // when the family had any non-zero client flow that day.
                      const pos = portfolio.positionChange;
                      const posUp = portfolio.hasComparison && pos > 0;
                      const posDown = portfolio.hasComparison && pos < 0;
                      const posColor = posUp ? CHANGE_UP : posDown ? CHANGE_DOWN : textMuted;
                      const flow = portfolio.netFlow;
                      const showFlow = flow != null && flow !== 0;
                      const title = portfolio.hasComparison
                        ? `Position ${formatSigned(pos)}` +
                          (showFlow ? `, flows ${formatSigned(flow)}` : '') +
                          `, ${shortDate(portfolio.prevAsOf)} -> ${shortDate(portfolio.asOf)}`
                        : undefined;
                      return (
                        <span
                          title={title}
                          style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}
                        >
                          <span style={{ color: textPrimary }}>
                            {formatMarketValue(portfolio.marketValue)}
                          </span>
                          {portfolio.hasComparison && (
                            <span
                              className="tabular-nums"
                              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, fontSize: 12 }}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: posColor }}>
                                {formatSigned(pos)}
                                {posUp && <ArrowUp size={11} />}
                                {posDown && <ArrowDown size={11} />}
                              </span>
                              {showFlow && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, color: flow > 0 ? CHANGE_UP : CHANGE_DOWN }}>
                                  {formatSigned(flow)}
                                  {flow > 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                                </span>
                              )}
                            </span>
                          )}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PortfolioOverviewPage;
